import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import BaseDialog from '../../../BaseDialog'

import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'
import AutoCompleteInput from '../../../AutoCompleteInput'
import DatePicker from '../../../../components/DatePicker'
import { format } from 'date-fns'

export default function CreateVehicleTypeDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation()

  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [type, setType] = useState('')
  const [specificity, setSpecificity] = useState('')
  const [constructeur, setConstructor] = useState(null)
  const [member, setMember] = useState(null)
  const [frameworkAgreement, setFrameworkAgreement] = useState(null)
  const [contractNumber, setContractNumber] = useState(null)
  const [lotNumber, setLotNumber] = useState(null)
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(null)
  const [purchaseOrderEmissionDate, setPurchaseOrderEmissionDate] =
    useState(null)
  const [totalVehicles, setTotalVehicles] = useState(null)
  const [excludeFromPvAdmission, setExcludeFromPvAdmission] = useState(false)
  const [pvAdmissionDocumentType, setPvAdmissionDocumentType] =
    useState('acquisition')
  const [copyItemsFromId, setCopyItemsFromId] = useState(null)

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError('')
    setType('')
    setSpecificity('')
    setConstructor(null)
    setMember(null)
    setFrameworkAgreement(null)
    setContractNumber(null)
    setLotNumber(null)
    setPurchaseOrderNumber(null)
    setPurchaseOrderEmissionDate(null)
    setTotalVehicles(null)
    setExcludeFromPvAdmission(false)
    setPvAdmissionDocumentType('acquisition')
    setCopyItemsFromId(null)
  }

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = () => {
    if (type.length === 0) {
      setError('type_required')
      return
    }

    if (specificity.length === 0) {
      setError('specificity_required')
      return
    }

    let data = !constructeur
      ? {
          type,
          specificity,
        }
      : {
          type,
          specificity,
          constructorId: constructeur.id,
        }
    if (member) {
      data.memberId = member.id
    }
    if (frameworkAgreement) {
      data.frameworkAgreement = frameworkAgreement
    }
    if (contractNumber) {
      data.contractNumber = contractNumber
    }
    if (lotNumber) {
      data.lotNumber = lotNumber
    }
    if (purchaseOrderNumber) {
      data.purchaseOrderNumber = purchaseOrderNumber
    }
    if (purchaseOrderEmissionDate) {
      data.purchaseOrderEmissionDate = format(
        purchaseOrderEmissionDate,
        'yyyy-MM-dd'
      )
    }
    if (totalVehicles) {
      data.totalVehicles = totalVehicles
    }
    data.excludeFromPvAdmission = excludeFromPvAdmission
    data.pvAdmissionDocumentType = pvAdmissionDocumentType
    if (copyItemsFromId) {
      data = { copyItemsFromId: copyItemsFromId.id, ...data }
    }
    setLoading(true)
    dataProvider
      .create('vehicle-type', { data })
      .then(() => {
        dataProvider.cache.reset()
        dataProvider.member.resetCache()
        showNotification('success', t('create-vehicle-type-dialog.success'))
        onCreated()
        resetState()
      })
      .catch((err) => {
        if (err.response.data.error === 'VEHICLE_TYPE_ALREADY_EXISTS') {
          showNotification(
            'error',
            t('create-vehicle-type-dialog.already-exists-error')
          )
        } else {
          showNotification('error', t('errors.api-error'))
        }
        setLoading(false)
      })
  }

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={t('create-vehicle-type-dialog.title')}
      loading={loading}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <TextField
            variant="standard"
            required
            error={error === 'type_required'}
            helperText={t('common.required')}
            id="type"
            label={t('create-vehicle-type-dialog.type')}
            fullWidth
            value={type}
            onChange={(event) => setType(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            required
            error={error === 'specificity_required'}
            helperText={t('common.required')}
            id="specificity"
            label={t('create-vehicle-type-dialog.specificity')}
            fullWidth
            value={specificity}
            onChange={(event) => setSpecificity(event.target.value)}
          />
        </Grid>
        <Grid item>
          <AutoCompleteInput
            onChange={setConstructor}
            value={constructeur}
            label={t('create-vehicle-type-dialog.constructor-label')}
            resource="/constructors/autocomplete"
            canDelete
          />
        </Grid>
        <Grid item>
          <AutoCompleteInput
            onChange={setMember}
            value={member}
            label={t('create-vehicle-type-dialog.member')}
            resource="vehicle-type/autocomplete-members"
            canDelete
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="frameworkAgreement"
            label={t('create-vehicle-type-dialog.framework-agreement')}
            fullWidth
            value={frameworkAgreement}
            onChange={(event) => setFrameworkAgreement(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="contractNumber"
            label={t('create-vehicle-type-dialog.contract-number')}
            fullWidth
            value={contractNumber}
            onChange={(event) => setContractNumber(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="lotNumber"
            label={t('create-vehicle-type-dialog.lot-number')}
            fullWidth
            value={lotNumber}
            onChange={(event) => setLotNumber(event.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="purchaseOrderNumber"
            label={t('create-vehicle-type-dialog.purchase-order-number')}
            fullWidth
            value={purchaseOrderNumber}
            onChange={(event) => setPurchaseOrderNumber(event.target.value)}
          />
        </Grid>
        <Grid item>
          <DatePicker
            id="purchaseOrderEmissionDate"
            value={purchaseOrderEmissionDate}
            onChange={setPurchaseOrderEmissionDate}
            label={t('create-vehicle-type-dialog.purchase-order-emission-date')}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="totalVehicles"
            label={t('create-vehicle-type-dialog.total-vehicle-number')}
            type="number"
            fullWidth
            value={totalVehicles}
            onChange={(event) => setTotalVehicles(event.target.value)}
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeFromPvAdmission}
                onChange={(event) =>
                  setExcludeFromPvAdmission(event.target.checked)
                }
                name="excludeFromPvAdmission"
              />
            }
            label={t('create-vehicle-type-dialog.exclude-from-pv-admission')}
          />
        </Grid>
        <Grid item>
          <FormControl fullWidth variant="standard">
            <InputLabel id="pvAdmissionDocumentType-label">
              {t('create-vehicle-type-dialog.pv-admission-document-type')}
            </InputLabel>
            <Select
              labelId="pvAdmissionDocumentType-label"
              id="pvAdmissionDocumentType"
              value={pvAdmissionDocumentType}
              onChange={(event) =>
                setPvAdmissionDocumentType(event.target.value)
              }
            >
              <MenuItem value="acquisition">
                {t(
                  'create-vehicle-type-dialog.pv-admission-document-type-acquisition'
                )}
              </MenuItem>
              <MenuItem value="location">
                {t(
                  'create-vehicle-type-dialog.pv-admission-document-type-location'
                )}
              </MenuItem>
              <MenuItem value="refaction">
                {t(
                  'create-vehicle-type-dialog.pv-admission-document-type-refaction'
                )}
              </MenuItem>
              <MenuItem value="reserve">
                {t(
                  'create-vehicle-type-dialog.pv-admission-document-type-reserve'
                )}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <AutoCompleteInput
            onChange={setCopyItemsFromId}
            value={copyItemsFromId}
            label={t('create-vehicle-type-dialog.copy-items')}
            resource="vehicle-type/autocomplete"
            canDelete
          />
        </Grid>
      </Grid>
    </BaseDialog>
  )
}
